// 본문(에디터/공개 페이지) 안 링크 클릭을 SPA 내비게이션으로 전환하기 위한 순수 로직.
//
// 배경 — 이슈 A: BlockNote 링크(link mark)는 기본적으로
//   1) 렌더 HTML 에 target="_blank" 를 강제로 붙이고 (Link 익스텐션 addOptions.HTMLAttributes)
//   2) 편집 가능 상태에서는 ProseMirror handleClick 플러그인이 네이티브 <a> 클릭을 가로채
//      window.open(href, target) 을 직접 호출한다
//   (@blocknote/core Link 익스텐션, node_modules/@blocknote/core/dist/src-*.js 의
//    addOptions()/handleClickLink 참조).
// 그래서 href="/page/<uuid>" 같은 "같은 앱 내부" 링크를 클릭해도 항상 새 창/탭이 열린다.
// 노션 기대 동작은 "본문만 전환, 좌측 사이드바 유지"(SPA 클라이언트 내비게이션)이므로,
// 에디터/공개 페이지 컨테이너에 클릭 캡처 리스너를 달아 내부 링크만 가로채 react-router
// navigate() 로 전환한다. 외부 링크·수정자 클릭(Cmd/Ctrl/가운데 클릭)은 기존 동작(새 탭)을 유지.
//
// 이 파일은 DOM 이벤트 객체 등 브라우저 API 에만 의존하고 React/BlockNote 를 import 하지
// 않는다 — 순수 로직만 분리해 단위 테스트 가능하게 유지(internalLinkNav.test.js 참조).

/**
 * href 가 "같은 origin(=내부)" 링크인지 판별하고, 맞다면 react-router 로 이동할
 * pathname+search+hash 문자열을 반환한다. 외부 링크·mailto:·tel: 등이거나 파싱 불가하면 null.
 *
 * - 상대 경로("/page/<uuid>")와 절대 URL(https://같은도메인/page/<uuid>) 모두 지원 —
 *   useCollab.js 의 페이지 링크 추출 로직과 동일하게, 사용자가 주소를 통째로 복사해
 *   붙여넣은 절대 URL 링크도 내부 링크로 인식해야 한다.
 * - http/https 가 아닌 스킴(mailto:, tel:, javascript: 등)은 항상 외부 취급(null).
 *
 * @param {string} href
 * @param {{ origin?: string }} [opts]
 * @returns {string|null}
 */
export function resolveInternalPath(href, opts) {
  if (!href || typeof href !== 'string') return null;
  const origin = (opts && opts.origin) || (typeof window !== 'undefined' ? window.location.origin : '');
  if (!origin) return null;
  let url;
  try {
    url = new URL(href, origin);
  } catch (e) {
    return null;
  }
  if (!/^https?:$/.test(url.protocol)) return null;
  if (url.origin !== origin) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * 클릭이 "새 탭/새 창 강제 허용" 대상인지 — 수정자 키(Cmd/Ctrl/Shift/Alt) 클릭이거나
 * 좌클릭이 아니면(가운데 클릭 등) true. 이 경우 우리는 아무것도 하지 않고 기존 동작에 맡긴다.
 * (참고: 실제 가운데클릭은 대부분 브라우저에서 'click' 이 아닌 'auxclick' 으로 발생해 이 핸들러
 *  자체를 타지 않지만, 일부 환경 방어를 위해 button 체크를 유지한다.)
 *
 * @param {{ metaKey?: boolean, ctrlKey?: boolean, shiftKey?: boolean, altKey?: boolean, button?: number }} event
 * @returns {boolean}
 */
export function isModifiedClick(event) {
  if (!event) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return true;
  return typeof event.button === 'number' && event.button !== 0;
}

/**
 * 클릭 이벤트에서 (root 컨테이너 내부의) 앵커 엘리먼트를 찾는다.
 * root 바깥의 앵커(포탈 등으로 렌더된 팝오버 안 링크)는 대상에서 제외.
 *
 * @param {Event} event
 * @param {Element|null} root
 * @returns {HTMLAnchorElement|null}
 */
export function findClickedAnchor(event, root) {
  const target = event && event.target;
  if (!target || !root) return null;
  const anchor = typeof target.closest === 'function' ? target.closest('a[href]') : null;
  if (!anchor) return null;
  if (typeof root.contains === 'function' && !root.contains(anchor)) return null;
  return anchor;
}

/**
 * 캡처 단계 클릭 리스너 팩토리.
 *
 * - 수정자 클릭/좌클릭이 아니면: 아무 것도 하지 않음(기본 동작 = 새 탭).
 * - 앵커가 아니거나 root 밖이면: 아무 것도 하지 않음.
 * - 외부 링크(다른 origin, 또는 http/https 가 아닌 스킴)면: 아무 것도 하지 않음(기존 새 탭 동작 유지).
 * - 내부 링크(같은 origin)면: preventDefault + stopPropagation 후 navigate(path).
 *   stopPropagation 이 핵심 — 이 리스너를 BlockNote 에디터 DOM 보다 상위 컨테이너에
 *   {capture:true} 로 등록해야, ProseMirror 의 handleClick(window.open 호출)이 이 이벤트를
 *   아예 받지 못하고 우리가 먼저 가로챈다.
 *
 * @param {(path: string) => void} navigate
 * @param {() => (Element|null)} getRoot
 * @returns {(event: MouseEvent) => void}
 */
export function createInternalLinkClickHandler(navigate, getRoot) {
  return function handleInternalLinkClick(event) {
    if (isModifiedClick(event)) return;
    const root = typeof getRoot === 'function' ? getRoot() : getRoot;
    const anchor = findClickedAnchor(event, root);
    if (!anchor) return;
    const path = resolveInternalPath(anchor.getAttribute('href'));
    if (!path) return; // 외부 링크 — 기존 동작(새 탭) 유지
    event.preventDefault();
    event.stopPropagation();
    navigate(path);
  };
}
